import { getAllFoldersForMoveTo } from '../helpers/getFoldersForMoveTo';
import MoveToMailboxContext from '../store/schema/MoveToMailboxContext';
import { getStore } from '../store/store';
import { getPrimaryMailboxRootFolder, getArchiveMailboxRootFolder, Folder } from 'owa-folders';
import { MoveToFolderType } from 'owa-mail-moveto-control';
import {
    getMailboxLoadingState,
    ARCHIVE_FOLDER_ROOT_DISTINGUISHED_ID,
    MailboxLoadStateEnum,
} from 'owa-folders';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';

/**
 * Get the root folder for given moveTo mailbox context
 */
export function getRootFolderForGivenContext(moveToMailboxContext: MoveToMailboxContext): Folder {
    switch (moveToMailboxContext) {
        case 'primary':
            return getPrimaryMailboxRootFolder();
        case 'archive':
            return getArchiveMailboxRootFolder();
        default:
            throw new Error(
                'To get root folder id, the context must be either primary or archive as other contexts like shared folders might be dynamic'
            );
    }
}

/**
 * Get the root folder ID for given moveTo mailbox context
 */
export function getRootFolderIdForGivenContext(moveToMailboxContext: MoveToMailboxContext): string {
    return getRootFolderForGivenContext(moveToMailboxContext).FolderId.Id;
}

/**
 * Determines whether to show "All folders" menu item depending on the moveToMailboxContext
 * @param mailboxContextToShow - name of the moveTo mailbox we want to get all the folders for. FYI: this context is not the same as current value of store's moveToMailboxContext
 * @param moveToContextsSupported - the supported move to contexts
 * @param foldersShown - folders that are already shown in default mode. As the folders of default mode are from primary mailbox only so this check is required
 * @param shouldShowAllFolders - should show all folders menu
 * @returns boolean - if a mailbox's all folders can be shown or not
 */
export function shouldShowAllFoldersMenuItem(
    mailboxContextToShow: MoveToMailboxContext,
    moveToContextsSupported: MoveToMailboxContext[],
    foldersShown: MoveToFolderType[],
    shouldShowAllFolders?: boolean
): boolean {
    const moveToStore = getStore();
    const currentMoveToContext = moveToStore.moveToMailboxContext;
    switch (mailboxContextToShow) {
        case 'primary':
            // primary mailbox is supported in implementation of MoveToFolder component; AND
            // is primary mailbox and move to menu with directly all folders(default) need to be displayed; AND
            // (1. currently moveto store is not in primary moveTo mailbox context; OR
            // 2. default view; OR
            // 3. if anything being searched in any context; OR
            // 4. context is primary but already shown folders are less than number of total folders in primary mailbox)
            return (
                moveToContextsSupported.indexOf('primary') > -1 &&
                (currentMoveToContext != 'primary' ||
                    (currentMoveToContext == 'primary' && !shouldShowAllFolders) ||
                    moveToStore.findText.length > 0 ||
                    (currentMoveToContext == 'primary' &&
                        foldersShown.length < getAllFoldersForMoveTo('primary').folders.length))
            );

        case 'archive':
            // archive mailbox is supported in implementation of MoveToFolder component; AND
            // archive mailbox is enabled for the user; AND
            // (1. currently moveto store is not in archive moveTo mailbox context(this includes includes default view); OR
            // 2. if anything being searched in any context; OR
            // 3. context is archive but already shown folders are less than number of total folders in archive mailbox; OR)
            return (
                moveToContextsSupported.indexOf('archive') > -1 &&
                !isConsumer() &&
                !!getArchiveMailboxRootFolder() &&
                (currentMoveToContext != 'archive' ||
                    moveToStore.findText.length > 0 ||
                    (currentMoveToContext == 'archive' &&
                        foldersShown.length < getAllFoldersForMoveTo('archive').folders.length))
            );

        case 'sharedFolder':
            // shared folders is supported in implementation of MoveToFolder component; AND
            // shared folders feature flag is present;
            return moveToContextsSupported.indexOf('sharedFolder') > -1 && !isConsumer();

        default:
            return false;
    }
}

/**
 * Determines whether we should show the loading spinner in move to control or not
 * @returns boolean - if a spinner should be shown or not
 */
export function shouldShowMoveToLoadingSpinner(): boolean {
    const { moveToMailboxContext, moveToSharedFolderRootId } = getStore();
    switch (moveToMailboxContext) {
        case 'archive':
            return (
                getMailboxLoadingState(ARCHIVE_FOLDER_ROOT_DISTINGUISHED_ID) ===
                MailboxLoadStateEnum.Loading
            );
        case 'sharedFolder':
            return (
                getMailboxLoadingState(moveToSharedFolderRootId) === MailboxLoadStateEnum.Loading
            );
        default:
            return false;
    }
}
